import { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ReBox from "../../../../common/ReBox";
import { GlobalContext } from "../../../../common/Context";
import LoadingWheel from "../../../../common/LoadingWheel";
import { useTranslation } from "react-i18next";
import { blue, green, white } from "../../../../../theme/colours";
import { GiftIcon } from "../../../../common/Icons";
import { formatCampaignEntries } from "../../../../common/Utils/Utils";
import { getCampaignDateRangeString } from "../../../../common/Utils/TimeUtils";
import { CHALLENGES_CARD_ID } from "../../../../../constants/ids";

const ChallengeCard = ({
  title,
  type,
  neededScore,
  startDate,
  endDate,
  prizeDesc,
  entries,
  banner,
  id,
}) => {
  const { largeView, tabletView, mobileView, desktopView, optionState } =
    useContext(GlobalContext);
  const { t } = useTranslation("CompetitionInfo");
  const [formattedEntries, setFormattedEntries] = useState();
  const [entryLoading, setEntryLoading] = useState(true);

  const challengeCardStyle = {
    p: 0,
    display: mobileView ? "flex" : "grid",
    gridTemplateColumns: "27.5% 72.5%",
    gridTemplateRows: "auto",
    borderRadius: "10px",
    mb: "1.5rem",
    height: "auto",
    width: tabletView || mobileView ? "100%" : "calc(50% - 0.5rem)",
    flexWrap: "wrap",
    background: desktopView && white,
    boxShadow: "none",
  };
  const imgBoxStyle = {
    gridColumn: "1 / 2",
    gridRow: "1 / span 2",
    pr: "1rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: mobileView ? "40%" : "100%",
  };
  const titlePrizeAreaStyle = {
    gridColumn: "2 / 3",
    gridRow: "1 / 2",
    p: "0.75rem 1.25rem 0",
    width: mobileView ? "60%" : "auto",
    display: mobileView && "flex",
    flexDirection: "column",
    justifyContent: "center",
    pl: mobileView ? 0 : "0.75rem",
  };
  const topTextStyle = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    mb: "0.5rem",
    alignItems: mobileView ? "flex-start" : "center",
    flexDirection: mobileView ? "column-reverse" : "row",
  };
  const ticketBoxStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: blue[1],
    borderRadius: "10px",
    minWidth: largeView ? "2.75rem" : "2.5rem",
    height: largeView ? "1.35rem" : "1.25rem",
    mb: mobileView && "1rem",
    p: "0 0.25rem",
  };
  const entriesTextStyle = {
    fontWeight: 500,
    color: white,
    fontSize: "0.75rem",
    lineHeight: 1,
    ml: "0.25rem",
  };
  const enterStyle = {
    fontWeight: 700,
    mr: "0.25rem",
  };
  const enterDescStyle = {
    fontWeight: 300,
  };
  const indentStyle = {
    mr: "0.25rem",
  };
  const descAreaStyle = {
    gridColumn: "2 / 3",
    gridRow: "2 / 3",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    pb: "0.5rem",
    mt: mobileView && "0.75rem",
    ml: mobileView ? 0 : "0.75rem",
  };
  const descBoxStyle = {
    display: "flex",
    justifyContent: "flex-start",
    mb: "0.75rem",
  };
  const giftBoxStyle = {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  };
  const challengesTextStyle = {
    mb: "0.5rem",
    fontStyle: "italic",
  };
  const botDescBoxStyle = {
    display: "flex",
    flexWrap: "wrap",
  };
  const giftIconStyle = { fill: green[2], width: "15px" };
  const giftCardTextStyle = {
    fontWeight: 600,
    color: green[2],
    fontSize: mobileView ? "0.875rem" : "0.75rem",
  };
  const bannerStyle = {
    width: "100%",
    height: "100%",
    borderRadius: "0.5rem",
    objectFit: "cover",
  };
  const dateStyle = {
    opacity: 0.4,
    fontStyle: "italic",
  };
  const runsFromStyle = {
    ...dateStyle,
    fontWeight: 500,
    mr: "0.25rem",
  };

  useEffect(() => {
    setFormattedEntries(formatCampaignEntries(entries));
    setEntryLoading(false);
  }, [optionState]);

  return (
    <ReBox
      style={challengeCardStyle}
      divProps={id && { id: CHALLENGES_CARD_ID(id) }}>
      <Box sx={imgBoxStyle}>
        <Box
          component="img"
          src={banner}
          alt="campaign image"
          sx={bannerStyle}
        />
      </Box>
      <Box sx={titlePrizeAreaStyle}>
        <Box sx={topTextStyle}>
          <Typography variant="h3" sx={mobileView && { fontSize: "0.875rem" }}>
            {title}
          </Typography>
          {!entryLoading ? (
            <Box sx={ticketBoxStyle}>
              <img
                src="images/ticket.svg"
                alt="ticket icon"
                width={largeView ? "15px" : "13px"}
                height="inherit"
              />
              <Typography sx={entriesTextStyle}>{formattedEntries}</Typography>
            </Box>
          ) : (
            <LoadingWheel width={3} />
          )}
        </Box>
        <Box sx={giftBoxStyle}>
          <Box sx={indentStyle}>
            <GiftIcon style={giftIconStyle} />
          </Box>
          <Typography sx={giftCardTextStyle}>{prizeDesc}</Typography>
        </Box>
      </Box>
      <Box sx={descAreaStyle}>
        <Box sx={descBoxStyle}>
          <Typography sx={enterStyle}>{"ENTER:"}</Typography>
          <Typography sx={enterDescStyle}>
            {`Get a ${type.toLowerCase()} score of ${neededScore}+.`}
          </Typography>
        </Box>
        <Typography sx={challengesTextStyle}>
          {t("oneEntryEveryDay")}
        </Typography>
        <Box sx={botDescBoxStyle}>
          <Typography sx={runsFromStyle}>{"Runs from:"}</Typography>
          <Typography sx={dateStyle}>
            {getCampaignDateRangeString(startDate, endDate)}
          </Typography>
        </Box>
      </Box>
    </ReBox>
  );
};

export default ChallengeCard;
